import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Badge from '@mui/material/Badge';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { FilterTrayMAIQProps, FilterTrayOption } from './types';

const PANEL_WIDTH_LEFT = 200;
const PANEL_WIDTH_RIGHT = 320;
const POPOVER_WIDTH = PANEL_WIDTH_LEFT + PANEL_WIDTH_RIGHT;
const POPOVER_HEIGHT = 480;
const HEADER_HEIGHT = 48;
const BOTTOM_BAR_HEIGHT = 48;
const CATEGORY_SKELETON_COUNT = 6;
const OPTION_SKELETON_COUNT = 8;

function FilterTrayCategoriesSkeleton() {
  return (
    <>
      {Array.from({ length: CATEGORY_SKELETON_COUNT }).map((_, index) => (
        <Box key={index} sx={{ px: 1.5, py: 1 }}>
          <Skeleton variant="text" width={`${55 + (index % 3) * 12}%`} height={20} />
        </Box>
      ))}
    </>
  );
}

function FilterTrayOptionsSkeleton() {
  return (
    <>
      <Box
        sx={{
          flexShrink: 0,
          p: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1 }} />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 0.5, px: 1 }}>
        {Array.from({ length: OPTION_SKELETON_COUNT }).map((_, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              py: 0.75,
              px: 0.5,
            }}
          >
            <Skeleton variant="rounded" width={18} height={18} sx={{ mt: 0.25, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton
                variant="text"
                width={`${60 + (index % 4) * 8}%`}
                height={18}
              />
              {index % 3 === 0 ? (
                <Skeleton variant="text" width="40%" height={14} sx={{ mt: 0.25 }} />
              ) : null}
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
}

/**
 * Generic FilterTray: Jira-style two-panel filter (categories left, options with checkboxes + search right).
 * Fully controlled: categories, options, selection and all callbacks come from parent.
 */
const FilterTrayMAIQ: React.FC<FilterTrayMAIQProps> = ({
  isLoading = false,
  open,
  onClose,
  anchorEl,
  categories,
  activeCategoryId,
  onActiveCategoryChange,
  optionsForActiveCategory,
  selectedByCategory,
  onSelectionChange,
  totalActiveCount,
  onClearAllFilters,
  onClearActiveCategory,
  onClearAllInPanel,
  customFiltersDropdown,
  searchPlaceholder,
  renderTrigger,
  renderActiveCategoryContent,
}) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');

  const activeCategory = useMemo(
    () => categories.find(c => c.id === activeCategoryId),
    [categories, activeCategoryId],
  );

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return optionsForActiveCategory;
    const q = searchQuery.trim().toLowerCase();
    return optionsForActiveCategory.filter(
      opt =>
        opt.label.toLowerCase().includes(q) ||
        (opt.subText && opt.subText.toLowerCase().includes(q)) ||
        opt.value.toLowerCase().includes(q),
    );
  }, [optionsForActiveCategory, searchQuery]);

  const activeSelection = activeCategoryId ? selectedByCategory[activeCategoryId] ?? [] : [];
  const hasActiveSelection = activeSelection.length > 0;

  const handleToggleOption = (categoryId: string, value: string, checked: boolean) => {
    const current = selectedByCategory[categoryId] ?? [];
    const next = checked
      ? [...current, value]
      : current.filter(v => v !== value);
    onSelectionChange(categoryId, next);
  };

  React.useEffect(() => {
    if (!open) setSearchQuery('');
  }, [open]);

  React.useEffect(() => {
    setSearchQuery('');
  }, [activeCategoryId]);

  const searchPlaceholderText = activeCategory
    ? (searchPlaceholder
        ? searchPlaceholder(activeCategory.label)
        : `Search ${activeCategory.label.toLowerCase()}`)
    : 'Search';

  const customActiveCategoryContent =
    activeCategoryId && activeCategory
      ? renderActiveCategoryContent?.({
          activeCategoryId,
          activeCategoryLabel: activeCategory.label,
          optionsForActiveCategory,
          selectedValuesForActiveCategory: activeSelection,
          onToggleOption: (value, checked) =>
            handleToggleOption(activeCategoryId, value, checked),
        })
      : null;
  const isUsingCustomActiveCategoryContent = customActiveCategoryContent != null;
  const showCategorySkeletons = isLoading && categories.length === 0;
  const showOptionsSkeletons =
    isLoading &&
    Boolean(activeCategoryId) &&
    !isUsingCustomActiveCategoryContent;

  return (
    <>
      {renderTrigger &&
        renderTrigger({
          onClick: () => {}, // Caller controls open via anchorEl; if using renderTrigger they typically use a button that sets anchor
          activeCount: totalActiveCount,
        })}

      <Popover
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: POPOVER_WIDTH,
            height: POPOVER_HEIGHT,
            maxHeight: '90vh',
            borderRadius: 1,
            boxShadow: theme.shadows[8],
            mt: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        {/* Sticky top bar: Clear filters */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.5,
            minHeight: HEADER_HEIGHT,
            borderBottom: 1,
            borderColor: 'divider',
            flexWrap: 'wrap',
            bgcolor: 'background.paper',
          }}
        >
          {customFiltersDropdown}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              onClick={onClearAllFilters}
              disabled={totalActiveCount === 0}
              sx={{ textTransform: 'none', minWidth: 0 }}
            >
              Clear filters
            </Button>
          </Box>
        </Box>

        {/* Middle: left (scrollable) + right (sticky search + scrollable list) */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            overflow: 'hidden',
          }}
        >
          {/* Left panel: scrollable categories only */}
          <Box
            sx={{
              width: PANEL_WIDTH_LEFT,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              overflowY: 'auto',
              py: 0.5,
            }}
          >
            {showCategorySkeletons ? (
              <FilterTrayCategoriesSkeleton />
            ) : (
              categories.map(cat => (
                <Box
                  key={cat.id}
                  onClick={() => onActiveCategoryChange(cat.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    borderLeft: 3,
                    borderColor: 'transparent',
                    bgcolor:
                      activeCategoryId === cat.id
                        ? 'action.hover'
                        : 'transparent',
                    borderLeftColor:
                      activeCategoryId === cat.id
                        ? 'primary.main'
                        : 'transparent',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                    {cat.label}
                  </Typography>
                  {cat.info && (
                    <InfoOutlinedIcon
                      sx={{ fontSize: 14, color: 'text.secondary' }}
                      aria-label={cat.info}
                    />
                  )}
                  {cat.selectedCount > 0 && (
                    <Badge
                      badgeContent={cat.selectedCount}
                      color="primary"
                      sx={{ '& .MuiBadge-badge': { fontSize: 10 } }}
                    />
                  )}
                </Box>
              ))
            )}
          </Box>

          {/* Right panel: sticky search + scrollable options */}
          <Box
            sx={{
              width: PANEL_WIDTH_RIGHT,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            {activeCategoryId ? (
              <>
                {customActiveCategoryContent ??
                  (showOptionsSkeletons ? (
                    <FilterTrayOptionsSkeleton />
                  ) : (
                    <>
                      {/* Sticky search at top of right panel */}
                      <Box
                        sx={{
                          flexShrink: 0,
                          p: 1.5,
                          borderBottom: 1,
                          borderColor: 'divider',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <TextField
                          size="small"
                          fullWidth
                          placeholder={searchPlaceholderText}
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                              </InputAdornment>
                            ),
                            sx: { backgroundColor: 'grey.50' },
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                        />
                      </Box>
                      {/* Scrollable checkbox list */}
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          overflowY: 'auto',
                          py: 0.5,
                          px: 1,
                        }}
                      >
                        {filteredOptions.map(opt => (
                          <FormControlLabel
                            key={opt.value}
                            control={
                              <Checkbox
                                size="small"
                                checked={activeSelection.includes(opt.value)}
                                onChange={e =>
                                  handleToggleOption(
                                    activeCategoryId,
                                    opt.value,
                                    e.target.checked,
                                  )
                                }
                                sx={{ py: 0.25 }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">{opt.label}</Typography>
                                {opt.subText && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {opt.subText}
                                  </Typography>
                                )}
                              </Box>
                            }
                            sx={{
                              alignItems: 'flex-start',
                              mx: 0,
                              py: 0.5,
                              '& .MuiFormControlLabel-label': { flex: 1 },
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  ))}
              </>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Select a category
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Sticky bottom bar: Clear all | X of Y | Clear */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            minHeight: BOTTOM_BAR_HEIGHT,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ flex: '0 0 auto' }}>
            {onClearAllInPanel ? (
              <Button
                size="small"
                onClick={onClearAllInPanel}
                disabled={totalActiveCount === 0}
                sx={{ textTransform: 'none' }}
              >
                Clear all
              </Button>
            ) : null}
          </Box>
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            {activeCategoryId && !isUsingCustomActiveCategoryContent ? (
              showOptionsSkeletons ? (
                <Skeleton variant="text" width={72} height={16} />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {filteredOptions.length} of {optionsForActiveCategory.length}
                </Typography>
              )
            ) : null}
          </Box>
          <Box sx={{ flex: '0 0 auto' }}>
            {activeCategoryId && !isUsingCustomActiveCategoryContent ? (
              <Button
                size="small"
                onClick={onClearActiveCategory}
                disabled={!hasActiveSelection}
                sx={{ textTransform: 'none' }}
              >
                Clear
              </Button>
            ) : null}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default FilterTrayMAIQ;
export type { FilterTrayMAIQProps, FilterTrayCategory, FilterTrayOption, FilterTraySelection } from './types';
